import { Suspense } from 'react';
import { NavLink, Redirect, Switch, useRouteMatch } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import { faArrowUpFromBracket, faHeartHalfStroke } from '@fortawesome/pro-regular-svg-icons';
import {
    faBuilding,
    faChartPieSimpleCircleDollar,
    faReceipt,
    faRepeat,
    faTimeline,
    faUsers,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RecurringPlans } from 'assets/js/pages/account-settings/imported-recurring-plans/RecurringPlans';
import { Draft } from 'assets/js/pages/account-settings/imports/Draft';
import { Drafts } from 'assets/js/pages/account-settings/imports/Drafts';
import { Imports } from 'assets/js/pages/account-settings/imports/Imports';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Import from 'components/imports/Import';
import { Route } from 'components/router/Route';
import { TestIds } from 'constants/tests';
import { useFlags } from 'launchdarkly-react-client-sdk';

const ImportsRoutes = {
    Imports: 'imports',
    Drafts: 'drafts',
    RecurringPlans: 'recurring-plans',
};

const ImportsRouter = () => {
    const match = useRouteMatch();
    const { softCreditImports, importFunds } = useFlags();

    return (
        <div className={'flex flex-col gap-4'}>
            <div className={'flex flex-wrap justify-between gap-x-8 gap-y-2'}>
                <div className={'flex items-stretch rounded-lg bg-gray-100 p-1'}>
                    <NavLink
                        exact={true}
                        to={match.url}
                        className={isActive =>
                            `group flex h-10 items-center gap-1.5 rounded-md bg-transparent px-4 py-2 text-base font-medium hover:text-inherit ${
                                isActive ? 'bg-white text-black drop-shadow-sm' : 'text-gray-600'
                            }`
                        }
                    >
                        History
                    </NavLink>
                    <NavLink
                        exact={true}
                        to={`${match.url}/${ImportsRoutes.Drafts}`}
                        data-testid={TestIds.AccountSettings.Imports.Drafts}
                        className={isActive =>
                            `group flex h-10 items-center gap-1.5 rounded-md bg-transparent px-4 py-2 text-base font-medium hover:text-inherit ${
                                isActive ? 'bg-white text-black drop-shadow-sm' : 'text-gray-600'
                            }`
                        }
                    >
                        Drafts
                    </NavLink>
                    <NavLink
                        exact={true}
                        to={`${match.url}/${ImportsRoutes.RecurringPlans}`}
                        data-testid={TestIds.AccountSettings.Imports.RecurringPlans}
                        className={isActive =>
                            `group flex h-10 items-center gap-1.5 rounded-md bg-transparent px-4 py-2 text-base font-medium hover:text-inherit ${
                                isActive ? 'bg-white text-black drop-shadow-sm' : 'text-gray-600'
                            }`
                        }
                    >
                        Recurring Plans
                    </NavLink>
                </div>
                <div className={'flex items-center justify-center space-x-6 md:justify-end'}>
                    <a
                        href={
                            'https://help.givebutter.com/en/collections/3855460-imports-migrations'
                        }
                        target={'_blank'}
                        rel={'noopener noreferrer'}
                    >
                        Help Center
                    </a>

                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild={true}>
                            <Button variant="solid">
                                New Import
                                <FontAwesomeIcon icon={faArrowUpFromBracket} />
                            </Button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content sideOffset={5} align={'end'} className={'max-w-sm'}>
                            <Suspense
                                fallback={
                                    <DropdownMenu.Item
                                        disabled={true}
                                        className={'flex items-center gap-x-2'}
                                    >
                                        <FontAwesomeIcon
                                            icon={faUsers}
                                            className={
                                                'text-brand-violet-primary rounded-lg border border-gray-100 p-2'
                                            }
                                        />
                                        <p>Contacts</p>
                                    </DropdownMenu.Item>
                                }
                            >
                                <Import.Root strategy="contacts">
                                    <Import.Trigger asChild={true}>
                                        <DropdownMenu.Item
                                            onSelect={e => e.preventDefault()}
                                            className={'flex items-center gap-x-2'}
                                        >
                                            <FontAwesomeIcon
                                                icon={faUsers}
                                                className={
                                                    'text-brand-violet-primary size-4 rounded-lg border border-gray-100 p-2'
                                                }
                                            />
                                            <p>Contacts</p>
                                        </DropdownMenu.Item>
                                    </Import.Trigger>
                                    <Import.Space />
                                </Import.Root>
                            </Suspense>

                            <Suspense
                                fallback={
                                    <DropdownMenu.Item
                                        disabled={true}
                                        className={'flex items-center gap-x-2'}
                                    >
                                        <FontAwesomeIcon
                                            icon={faBuilding}
                                            className={
                                                'text-brand-violet-primary rounded-lg border border-gray-100 p-2'
                                            }
                                        />
                                        <p>Companies</p>
                                    </DropdownMenu.Item>
                                }
                            >
                                <Import.Root strategy="companies">
                                    <Import.Trigger asChild={true}>
                                        <DropdownMenu.Item
                                            onSelect={e => e.preventDefault()}
                                            className={'flex items-center gap-x-2'}
                                        >
                                            <FontAwesomeIcon
                                                icon={faBuilding}
                                                className={
                                                    'text-brand-violet-primary size-4 rounded-lg border border-gray-100 p-2'
                                                }
                                            />
                                            <p>Companies</p>
                                        </DropdownMenu.Item>
                                    </Import.Trigger>
                                    <Import.Space />
                                </Import.Root>
                            </Suspense>

                            <Suspense
                                fallback={
                                    <DropdownMenu.Item
                                        disabled={true}
                                        className={'flex items-center gap-x-2'}
                                    >
                                        <FontAwesomeIcon
                                            icon={faTimeline}
                                            className={
                                                'text-brand-violet-primary rounded-lg border border-gray-100 p-2'
                                            }
                                        />
                                        <p>Contact activities</p>
                                    </DropdownMenu.Item>
                                }
                            >
                                <Import.Root strategy="contact_activities">
                                    <Import.Trigger asChild={true}>
                                        <DropdownMenu.Item
                                            onSelect={e => e.preventDefault()}
                                            className={'flex items-center gap-x-2'}
                                        >
                                            <FontAwesomeIcon
                                                icon={faTimeline}
                                                className={
                                                    'text-brand-violet-primary size-4 rounded-lg border border-gray-100 p-2'
                                                }
                                            />
                                            <p>Contact activities</p>
                                        </DropdownMenu.Item>
                                    </Import.Trigger>
                                    <Import.Space />
                                </Import.Root>
                            </Suspense>

                            <Suspense
                                fallback={
                                    <DropdownMenu.Item
                                        disabled={true}
                                        className={'flex items-center gap-x-2'}
                                    >
                                        <FontAwesomeIcon
                                            icon={faReceipt}
                                            className={
                                                'text-brand-violet-primary size-4 rounded-lg border border-gray-100 p-2'
                                            }
                                        />
                                        <p>Transactions</p>
                                    </DropdownMenu.Item>
                                }
                            >
                                <Import.Root strategy="transactions">
                                    <Import.Trigger asChild={true}>
                                        <DropdownMenu.Item
                                            onClick={e => e.preventDefault()}
                                            className={'flex items-center gap-x-2'}
                                        >
                                            <FontAwesomeIcon
                                                icon={faReceipt}
                                                className={
                                                    'text-brand-violet-primary size-4 rounded-lg border border-gray-100 p-2'
                                                }
                                            />
                                            <p>Transactions</p>
                                        </DropdownMenu.Item>
                                    </Import.Trigger>
                                    <Import.Space />
                                </Import.Root>
                            </Suspense>

                            <Suspense
                                fallback={
                                    <DropdownMenu.Item
                                        disabled={true}
                                        className={'flex items-center gap-x-2'}
                                    >
                                        <FontAwesomeIcon
                                            icon={faRepeat}
                                            className={
                                                'text-brand-violet-primary rounded-lg border border-gray-100 p-2'
                                            }
                                        />
                                        <p>Recurring Plans</p>
                                    </DropdownMenu.Item>
                                }
                            >
                                <Import.Root strategy="transaction_plans">
                                    <Import.Trigger asChild={true}>
                                        <DropdownMenu.Item
                                            onClick={e => e.preventDefault()}
                                            className={'flex items-center gap-x-2'}
                                        >
                                            <FontAwesomeIcon
                                                icon={faRepeat}
                                                className={
                                                    'text-brand-violet-primary rounded-lg border border-gray-100 p-2'
                                                }
                                            />
                                            <p>Recurring plans</p>
                                        </DropdownMenu.Item>
                                    </Import.Trigger>
                                    <Import.Space />
                                </Import.Root>
                            </Suspense>

                            {softCreditImports && (
                                <Suspense
                                    fallback={
                                        <DropdownMenu.Item
                                            disabled={true}
                                            className={'flex items-center gap-x-2'}
                                        >
                                            <FontAwesomeIcon
                                                icon={faHeartHalfStroke}
                                                className={
                                                    'text-brand-violet-primary rounded-lg border border-gray-100 p-2'
                                                }
                                            />
                                            <p>Soft credits</p>
                                        </DropdownMenu.Item>
                                    }
                                >
                                    <Import.Root strategy="soft_credits">
                                        <Import.Trigger asChild={true}>
                                            <DropdownMenu.Item
                                                onSelect={e => e.preventDefault()}
                                                className={'flex items-center gap-x-2'}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faHeartHalfStroke}
                                                    className={
                                                        'text-brand-violet-primary size-4 rounded-lg border border-gray-100 p-2'
                                                    }
                                                />
                                                <p>Soft credits</p>
                                            </DropdownMenu.Item>
                                        </Import.Trigger>
                                        <Import.Space />
                                    </Import.Root>
                                </Suspense>
                            )}

                            {importFunds && (
                                <Suspense
                                    fallback={
                                        <DropdownMenu.Item
                                            disabled={true}
                                            className={'flex items-center gap-x-2'}
                                        >
                                            <FontAwesomeIcon
                                                icon={faChartPieSimpleCircleDollar}
                                                className={
                                                    'text-brand-violet-primary rounded-lg border border-gray-100 p-2'
                                                }
                                            />
                                            <p>Funds</p>
                                        </DropdownMenu.Item>
                                    }
                                >
                                    <Import.Root strategy="funds">
                                        <Import.Trigger asChild={true}>
                                            <DropdownMenu.Item
                                                onSelect={e => e.preventDefault()}
                                                className={'flex items-center gap-x-2'}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faChartPieSimpleCircleDollar}
                                                    className={
                                                        'text-brand-violet-primary size-4 rounded-lg border border-gray-100 p-2'
                                                    }
                                                />
                                                <p>Funds</p>
                                            </DropdownMenu.Item>
                                        </Import.Trigger>
                                        <Import.Space />
                                    </Import.Root>
                                </Suspense>
                            )}
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>
                </div>
            </div>
            <Switch>
                <Route exact={true} path={match.path}>
                    <Imports />
                </Route>
                <Route exact={true} path={`${match.path}/${ImportsRoutes.Drafts}/:import_id(\\d+)`}>
                    <Draft />
                </Route>
                <Route exact={true} path={`${match.path}/${ImportsRoutes.Drafts}`}>
                    <Drafts />
                </Route>
                <Route exact={true} path={`${match.path}/${ImportsRoutes.RecurringPlans}`}>
                    <RecurringPlans />
                </Route>
                <Route>
                    <Redirect to={match.url} />
                </Route>
            </Switch>
        </div>
    );
};

export { ImportsRouter };
